/**
 * React Query hooks for store onboarding (CLA-993): the wholesale-account
 * application a clinic completes to get the store unlocked (CLA-980).
 *
 * Clinic side: read status, save a draft, submit for review.
 * Admin side: the review queue + approve/reject.
 *
 * Backend: lib/claro_web/controllers/api/v1/store_onboarding_controller.ex
 *          lib/claro_web/controllers/api/v1/store_onboarding_admin_controller.ex
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

export type StoreOnboardingStatus = 'draft' | 'submitted' | 'approved' | 'rejected'

export interface StoreOnboardingApplication {
  id: number
  clinic_id: number
  applicant_user_id: number
  status: StoreOnboardingStatus
  abn: string | null
  abr_snapshot: Record<string, unknown> | null
  legal_name: string | null
  trading_name: string | null
  delivery_address_line1: string | null
  delivery_address_line2: string | null
  delivery_suburb: string | null
  delivery_state: string | null
  delivery_postcode: string | null
  business_premises_attested: boolean
  attested_at: string | null
  prescriber_name: string | null
  prescriber_ahpra: string | null
  submitted_at: string | null
  reviewed_at: string | null
  reject_reason: string | null
  created_at: string
  // Admin queue enrichment only
  clinic_name?: string | null
  applicant_name?: string | null
  applicant_email?: string | null
}

export interface StoreOnboardingResponse {
  application: StoreOnboardingApplication | null
  store_unlocked: boolean
}

export interface StoreOnboardingDraft {
  abn?: string
  legal_name?: string
  trading_name?: string
  delivery_address_line1?: string
  delivery_address_line2?: string
  delivery_suburb?: string
  delivery_state?: string
  delivery_postcode?: string
  business_premises_attested?: boolean
}

const ONBOARDING_KEY = ['store', 'onboarding']
const QUEUE_KEY = ['admin', 'store-onboarding']

export function useStoreOnboarding(enabled = true) {
  return useQuery({
    queryKey: ONBOARDING_KEY,
    queryFn: () => api.get<StoreOnboardingResponse>('/store/onboarding'),
    enabled,
  })
}

export function useSaveStoreOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draft: StoreOnboardingDraft) =>
      api.put<StoreOnboardingResponse>('/store/onboarding', draft),
    onSuccess: data => qc.setQueryData(ONBOARDING_KEY, data),
  })
}

export function useSubmitStoreOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<StoreOnboardingResponse>('/store/onboarding/submit'),
    onSuccess: data => qc.setQueryData(ONBOARDING_KEY, data),
  })
}

// ── Admin queue ──────────────────────────────────────────────────

export function useStoreOnboardingQueue() {
  return useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () =>
      api.get<{ applications: StoreOnboardingApplication[] }>('/admin/store-onboarding'),
  })
}

export function useApproveStoreOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ application: StoreOnboardingApplication }>(
        `/admin/store-onboarding/${id}/approve`
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUEUE_KEY }),
  })
}

export function useRejectStoreOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post<{ application: StoreOnboardingApplication }>(
        `/admin/store-onboarding/${id}/reject`,
        { reason }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUEUE_KEY }),
  })
}
